import React, { useEffect, useMemo, useRef, useState } from "react";
import { Focus, Home, Settings } from "lucide-react";
import { useShallow } from "zustand/react/shallow";
import Tooltip from "../ui/Tooltip";
import {
  CAMERA_VIEWPOINTS,
  focusCameraOnSelection,
  moveCameraToDirection,
  resetCameraToHomeOrDefault,
} from "../../runtime/cameraViewActions";
import { CAMERA_VIEW_MENU_SHORTCUT_PULSE_EVENT } from "../../runtime/cameraViewMenuShortcutPulse";
import {
  CAMERA_VIEW_SETTINGS_KEY,
  readCameraViewPreferences,
} from "../../runtime/cameraViewPreferences";
import {
  readReducedMotionPreference,
  subscribeReducedMotionPreference,
} from "../../utils/reducedMotionPreference";
import { useSelectionStore } from "../../stores/selectionStore";
import { useSceneStore } from "../../stores/sceneStore";
import {
  getFloorAppearanceOptions,
  getFloorAppearancePalette,
  subscribeFloorAppearanceOptions,
} from "../../utils/floorAppearanceOptions";

function colorNumberToHex(color) {
  return `#${Number(color || 0)
    .toString(16)
    .padStart(6, "0")
    .slice(-6)}`;
}

function normalizeHexColor(color) {
  if (typeof color !== "string") return "";
  const match = /^#?([0-9a-f]{6})$/i.exec(color.trim());
  return match ? `#${match[1].toLowerCase()}` : "";
}

function relativeLuminance(colorHex) {
  const normalized = normalizeHexColor(colorHex);
  if (!normalized) return 1;
  const channels = [1, 3, 5].map((index) => {
    const value = Number.parseInt(normalized.slice(index, index + 2), 16) / 255;
    return value <= 0.03928 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * channels[0] + 0.7152 * channels[1] + 0.0722 * channels[2];
}

function isCameraMenuOnDarkScene(background, floorAppearance) {
  const floorPalette = getFloorAppearancePalette(floorAppearance);
  const floorHex =
    normalizeHexColor(floorAppearance.floorColorHex) || colorNumberToHex(floorPalette.floorColor);
  if (background?.type !== "color") return relativeLuminance(floorHex) < 0.38;

  const backgroundHex = normalizeHexColor(background.colorHex) || "#111827";
  const floorOpacity = Number.isFinite(Number(floorPalette.floorOpacity))
    ? Math.max(0, Math.min(1, Number(floorPalette.floorOpacity)))
    : 1;
  const mixedLuminance =
    relativeLuminance(floorHex) * floorOpacity +
    relativeLuminance(backgroundHex) * (1 - floorOpacity);
  return mixedLuminance < 0.38;
}

function ViewButton({
  label,
  slotClassName = "",
  buttonClassName = "",
  children,
  disabled = false,
  showSlash = false,
  shortcutPulseActive = false,
  onClick,
}) {
  return (
    <div className={`camera-view-menu-slot ${slotClassName}`}>
      <Tooltip content={label} side="top">
        <button
          type="button"
          className={`camera-view-menu-button ${buttonClassName}${
            shortcutPulseActive ? " camera-view-menu-button-shortcut-pulse" : ""
          }`}
          aria-label={label}
          disabled={disabled}
          onClick={onClick}
        >
          <span className="camera-view-menu-button-content">{children}</span>
          {showSlash && <span className="camera-view-menu-button-slash" aria-hidden />}
        </button>
      </Tooltip>
    </div>
  );
}

export default function CameraViewMenu() {
  const menuRef = useRef(null);
  const pulseClearRef = useRef(null);
  const [shortcutPulse, setShortcutPulse] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settings, setSettings] = useState(readCameraViewPreferences);
  const [reducedMotion, setReducedMotion] = useState(readReducedMotionPreference);
  const [floorAppearance, setFloorAppearance] = useState(() => getFloorAppearanceOptions());
  const background = useSceneStore((state) => state.background);
  const selection = useSelectionStore(
    useShallow((state) => ({
      selectedBody: state.selectedBody,
      selectedMesh: state.selectedMesh,
      selectedJoint: state.selectedJoint,
      selectedSite: state.selectedSite,
      selectedGeom: state.selectedGeom,
      selectedInertial: state.selectedInertial,
    }))
  );
  const hasAutofocusTarget = Boolean(
    selection.selectedBody ||
    selection.selectedMesh ||
    selection.selectedJoint ||
    selection.selectedSite ||
    selection.selectedGeom ||
    selection.selectedInertial
  );

  useEffect(() => {
    const handler = (event) => {
      const detail = event.detail;
      if (!detail || typeof detail !== "object" || !detail.kind) return;
      if (pulseClearRef.current) window.clearTimeout(pulseClearRef.current);
      setShortcutPulse(detail);
      pulseClearRef.current = window.setTimeout(() => {
        setShortcutPulse(null);
        pulseClearRef.current = null;
      }, 380);
    };
    window.addEventListener(CAMERA_VIEW_MENU_SHORTCUT_PULSE_EVENT, handler);
    return () => {
      window.removeEventListener(CAMERA_VIEW_MENU_SHORTCUT_PULSE_EVENT, handler);
      if (pulseClearRef.current) window.clearTimeout(pulseClearRef.current);
    };
  }, []);

  useEffect(() => {
    try {
      window.localStorage.setItem(CAMERA_VIEW_SETTINGS_KEY, JSON.stringify(settings));
    } catch {
      // Ignore localStorage failures; the menu still works for the current session.
    }
  }, [settings]);

  useEffect(() => subscribeReducedMotionPreference(setReducedMotion), []);

  useEffect(() => {
    return subscribeFloorAppearanceOptions(() => {
      setFloorAppearance({ ...getFloorAppearanceOptions() });
    });
  }, []);

  useEffect(() => {
    if (!settingsOpen) return undefined;
    const handlePointerDown = (event) => {
      if (menuRef.current?.contains(event.target)) return;
      setSettingsOpen(false);
    };
    document.addEventListener("pointerdown", handlePointerDown, true);
    return () => document.removeEventListener("pointerdown", handlePointerDown, true);
  }, [settingsOpen]);

  const setAutofocusMode = (autofocusMode) => {
    setSettings((current) => ({ ...current, autofocusMode }));
  };

  const setMotionMode = (motionMode) => {
    setSettings((current) => ({ ...current, motionMode }));
  };
  const effectiveSettings = reducedMotion ? { ...settings, motionMode: "jump" } : settings;
  const smoothMotionTooltip = reducedMotion
    ? "Smooth camera motion is disabled while Reduced Motion is on."
    : "Smooth camera transitions";
  const contrastClassName = isCameraMenuOnDarkScene(background, floorAppearance)
    ? "camera-view-menu-on-dark"
    : "camera-view-menu-on-light";

  const viewpointButtons = useMemo(
    () =>
      CAMERA_VIEWPOINTS.map((viewpoint) => (
        <ViewButton
          key={viewpoint.key}
          label={viewpoint.label}
          slotClassName={`camera-view-menu-${viewpoint.gridArea}`}
          buttonClassName="camera-view-menu-point"
          shortcutPulseActive={
            shortcutPulse?.kind === "orthogonal" && shortcutPulse.key === viewpoint.key
          }
          onClick={() =>
            moveCameraToDirection(viewpoint.direction, viewpoint.up, {
              motionMode: effectiveSettings.motionMode,
            })
          }
        >
          {viewpoint.text}
        </ViewButton>
      )),
    [effectiveSettings.motionMode, shortcutPulse]
  );

  return (
    <div
      ref={menuRef}
      className={[
        "camera-view-menu",
        contrastClassName,
        shortcutPulse?.kind === "fit" ? "camera-view-menu-fit-pulse" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      role="toolbar"
      aria-label="Camera views"
    >
      <div className="camera-view-menu-orbit" aria-hidden />
      <div className="camera-view-menu-axis camera-view-menu-axis-x" aria-hidden />
      <div className="camera-view-menu-axis camera-view-menu-axis-y" aria-hidden />
      {viewpointButtons}
      <div className="camera-view-menu-secondary-divider" aria-hidden />
      <ViewButton
        label={hasAutofocusTarget ? "Focus selected object" : "Select an object to autofocus"}
        slotClassName="camera-view-menu-focus"
        buttonClassName={`camera-view-menu-action${!hasAutofocusTarget ? " camera-view-menu-action-disabled" : ""}`}
        disabled={!hasAutofocusTarget}
        showSlash={!hasAutofocusTarget}
        shortcutPulseActive={shortcutPulse?.kind === "focus"}
        onClick={() => focusCameraOnSelection(selection, effectiveSettings)}
      >
        <Focus className="h-3.5 w-3.5" />
      </ViewButton>
      <ViewButton
        label="Reset camera view"
        slotClassName="camera-view-menu-reset"
        buttonClassName="camera-view-menu-action"
        shortcutPulseActive={shortcutPulse?.kind === "reset"}
        onClick={() => resetCameraToHomeOrDefault(effectiveSettings)}
      >
        <Home className="h-3.5 w-3.5" />
      </ViewButton>
      <div className="camera-view-menu-slot camera-view-menu-settings-gear">
        <Tooltip content="Camera view settings" side="top">
          <button
            type="button"
            className="camera-view-menu-button camera-view-menu-action camera-view-menu-settings-button"
            aria-label="Camera view settings"
            aria-expanded={settingsOpen}
            onClick={() => setSettingsOpen((open) => !open)}
          >
            <Settings className="h-3 w-3" />
          </button>
        </Tooltip>
      </div>
      {settingsOpen && (
        <div className="camera-view-menu-settings" role="menu" aria-label="Camera view settings">
          <div className="camera-view-menu-settings-section">
            <div className="camera-view-menu-settings-label">Autofocus</div>
            <div className="camera-view-menu-settings-row">
              <Tooltip content="Center selected object" side="top" fullWidth>
                <button
                  type="button"
                  className={`camera-view-menu-settings-option${
                    settings.autofocusMode === "center"
                      ? " camera-view-menu-settings-option-active"
                      : ""
                  }`}
                  onClick={() => setAutofocusMode("center")}
                >
                  Center
                </button>
              </Tooltip>
              <Tooltip content="Zoom to selected object" side="top" fullWidth>
                <button
                  type="button"
                  className={`camera-view-menu-settings-option${
                    settings.autofocusMode === "fit"
                      ? " camera-view-menu-settings-option-active"
                      : ""
                  }`}
                  onClick={() => setAutofocusMode("fit")}
                >
                  Frame
                </button>
              </Tooltip>
            </div>
          </div>
          <div className="camera-view-menu-settings-section">
            <div className="camera-view-menu-settings-label">Motion</div>
            <div className="camera-view-menu-settings-row">
              <Tooltip content={smoothMotionTooltip} side="top" fullWidth>
                <button
                  type="button"
                  className={`camera-view-menu-settings-option${
                    effectiveSettings.motionMode === "smooth"
                      ? " camera-view-menu-settings-option-active"
                      : ""
                  }`}
                  disabled={reducedMotion}
                  aria-label={smoothMotionTooltip}
                  onClick={() => setMotionMode("smooth")}
                >
                  Smooth
                </button>
              </Tooltip>
              <Tooltip content="Jump immediately between camera views" side="top" fullWidth>
                <button
                  type="button"
                  className={`camera-view-menu-settings-option${
                    effectiveSettings.motionMode === "jump"
                      ? " camera-view-menu-settings-option-active"
                      : ""
                  }`}
                  onClick={() => setMotionMode("jump")}
                >
                  Jump
                </button>
              </Tooltip>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
